import { useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';

// --- Client-side throttle / cooldown ---------------------------------------
// The `track-event` edge function rate-limits per session/IP and returns 429
// with a `retryAfter` (seconds). To avoid spamming it (and surfacing repeated
// runtime errors), we:
//   1. De-dupe identical events within a short window (per session).
//   2. Honour a global cooldown when the server replies 429.
let cooldownUntil = 0;
const lastSentAt = new Map<string, number>();
const DEDUPE_WINDOW_MS = 5000;

function shouldSkip(key: string): boolean {
  const now = Date.now();
  if (now < cooldownUntil) return true;
  const prev = lastSentAt.get(key);
  if (prev && now - prev < DEDUPE_WINDOW_MS) return true;
  lastSentAt.set(key, now);
  return false;
}

function handleRateLimit(response: any) {
  // Edge function shape: { ok:false, error:{ code:4001, retryAfter:<sec> } }
  const err: any = response?.error ?? response?.data?.error;
  const retry = err?.retryAfter ?? response?.data?.error?.retryAfter;
  if (retry && typeof retry === 'number') {
    cooldownUntil = Date.now() + retry * 1000;
  } else if (err?.code === 4001 || /Too many tracking/i.test(err?.message ?? '')) {
    cooldownUntil = Date.now() + 60_000;
  }
}

// Generate a session ID that persists for the session
const getSessionId = () => {
  let sessionId = sessionStorage.getItem('analytics_session_id');
  if (!sessionId) {
    sessionId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem('analytics_session_id', sessionId);
  }
  return sessionId;
};

// Parse UTM parameters from URL
const getUTMParams = () => {
  const params = new URLSearchParams(window.location.search);
  return {
    utmSource: params.get('utm_source') || undefined,
    utmMedium: params.get('utm_medium') || undefined,
    utmCampaign: params.get('utm_campaign') || undefined,
    utmContent: params.get('utm_content') || undefined,
    utmTerm: params.get('utm_term') || undefined,
  };
};

interface TrackEventOptions {
  templateId?: number;
  orderId?: number;
  promoCode?: string;
  referralCode?: string;
  bundleId?: string;
  properties?: Record<string, any>;
}

export const useAnalyticsTracking = () => {
  const sessionId = getSessionId();

  const trackEvent = useCallback(async (
    eventType: 
      | 'ad_click' 
      | 'landing_view' 
      | 'buy_click' 
      | 'qr_shown' 
      | 'paid' 
      | 'download' 
      | 'template_view'
      | 'add_to_cart'
      | 'checkout_start'
      | 'payment_success'
      | 'promo_applied'
      | 'referral_attributed'
      | 'bundle_added'
      | 'todo_generated'
      | 'todo_click_template'
      | 'todo_click_ats'
      | 'diagnostic_started'
      | 'diagnostic_completed'
      | 'referral_share_opened'
      | 'referral_link_copied'
      | 'referral_link_opened',
    options?: TrackEventOptions
  ) => {
    try {
      const dedupeKey = `${eventType}:${JSON.stringify(options?.properties ?? {})}`;
      if (shouldSkip(dedupeKey)) return;
      const { data: { session } } = await supabase.auth.getSession();
      const utmParams = getUTMParams();

      const response = await supabase.functions.invoke('track-event', {
        body: {
          sessionId,
          eventType,
          templateId: options?.templateId,
          orderId: options?.orderId,
          promoCode: options?.promoCode,
          referralCode: options?.referralCode,
          bundleId: options?.bundleId,
          ...utmParams,
          referrer: document.referrer,
          properties: options?.properties,
        },
      });

      if (response.error || (response.data && (response.data as any).ok === false)) {
        handleRateLimit(response);
      }
    } catch {
      // Never surface analytics errors — fire and forget.
    }
  }, [sessionId]);

  // Track landing page view on mount
  useEffect(() => {
    const path = window.location.pathname;
    
    // Track landing view for main pages
    if (path === '/' || path === '/templates') {
      trackEvent('landing_view');
    }
  }, [trackEvent]);

  // New tracking methods
  const trackCheckoutStart = useCallback(async (orderId: number, templateId?: number) => {
    await trackEvent('checkout_start', { orderId, templateId });
  }, [trackEvent]);
  
  const trackPaymentSuccess = useCallback(async (orderId: number, templateId?: number) => {
    await trackEvent('payment_success', { orderId, templateId });
  }, [trackEvent]);
  
  const trackPromoApplied = useCallback(async (promoCode: string, orderId: number) => {
    await trackEvent('promo_applied', { promoCode, orderId });
  }, [trackEvent]);
  
  const trackReferral = useCallback(async (referralCode: string) => {
    await trackEvent('referral_attributed', { referralCode });
  }, [trackEvent]);
  
  const trackBundleAdded = useCallback(async (bundleId: string) => {
    await trackEvent('bundle_added', { bundleId });
  }, [trackEvent]);

  return { 
    trackEvent,
    trackCheckoutStart,
    trackPaymentSuccess,
    trackPromoApplied,
    trackReferral,
    trackBundleAdded
  };
};

// Standalone function for tracking events without hook
export const trackAnalyticsEvent = async (
  eventType: 
    | 'ad_click' 
    | 'landing_view' 
    | 'buy_click' 
    | 'qr_shown' 
    | 'paid' 
    | 'download' 
    | 'template_view'
    | 'add_to_cart'
    | 'checkout_start'
    | 'payment_success'
    | 'promo_applied'
    | 'referral_attributed'
    | 'bundle_added'
    | 'todo_generated'
    | 'todo_click_template'
    | 'todo_click_ats'
    | 'diagnostic_started'
    | 'diagnostic_completed'
    | 'referral_share_opened'
    | 'referral_link_copied'
    | 'referral_link_opened',
  options?: TrackEventOptions
) => {
  try {
    const dedupeKey = `${eventType}:${JSON.stringify(options?.properties ?? {})}`;
    if (shouldSkip(dedupeKey)) return;
    const sessionId = getSessionId();
    const utmParams = getUTMParams();

    const response = await supabase.functions.invoke('track-event', {
      body: {
        sessionId,
        eventType,
        templateId: options?.templateId,
        orderId: options?.orderId,
        promoCode: options?.promoCode,
        referralCode: options?.referralCode,
        bundleId: options?.bundleId,
        ...utmParams,
        referrer: document.referrer,
        properties: options?.properties,
      },
    });

    if (response.error || (response.data && (response.data as any).ok === false)) {
      handleRateLimit(response);
    }
  } catch {
    // Never surface analytics errors — fire and forget.
  }
};